// ══════════════════════════════════════════════════════════════════════════════
// BOUTIQUE PRO - PAGE PUBLIQUE
// ══════════════════════════════════════════════════════════════════════════════
// Page vitrine accessible publiquement pour chaque professionnel
// Modes : marketplace (avec navigation AbracadaWorld) ou standalone (domaine perso)

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  MapPin, Phone, Mail, Globe, Clock,
  Facebook, Instagram, Linkedin, Twitter, Youtube,
  Star, MessageSquare, ChevronRight, ExternalLink,
  Package, Wrench, Heart, Share2, CheckCircle,
  ArrowLeft, Home, Search, ShoppingBag, FileText, Image
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════

export default function PublicStorePage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [storeData, setStoreData] = useState(null);
  const [activeTab, setActiveTab] = useState('about');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    service_interested: ''
  });
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);

  // Charger les données de la boutique
  useEffect(() => {
    const fetchStore = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/public/store/${slug}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('Boutique non trouvée');
          } else {
            setError('Erreur de chargement');
          }
          return;
        }
        
        const data = await response.json();
        setStoreData(data);
      } catch (err) {
        console.error('Erreur chargement boutique:', err);
        setError('Erreur de connexion');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchStore();
    }
  }, [slug]);

  // Envoyer le formulaire de contact
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSending(true);

    try {
      const response = await fetch(`${API_URL}/api/public/store/${slug}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });

      if (response.ok) {
        setContactSent(true);
        setContactForm({ name: '', email: '', phone: '', message: '', service_interested: '' });
      }
    } catch (err) {
      console.error('Erreur envoi contact:', err);
    } finally {
      setContactSending(false);
    }
  };

  // État de chargement
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement de la boutique...</p>
        </div>
      </div>
    );
  }

  // Erreur
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">🏪</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">{error}</h1>
          <p className="text-gray-600 mb-6">Cette boutique n'existe pas ou n'est plus disponible.</p>
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Search size={18} />
            Explorer le marketplace
          </Link>
        </div>
      </div>
    );
  }

  const { store, products, services, reviews, social_posts, display_mode } = storeData;
  const theme = store.theme || {};
  const settings = store.settings || {};
  const isMarketplace = display_mode === 'marketplace';

  // Styles dynamiques basés sur le thème
  const primaryColor = theme.primary_color || '#3B82F6';
  const secondaryColor = theme.secondary_color || '#1E40AF';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ══════════════════════════════════════════════════════════════════════
          NAVIGATION MARKETPLACE (uniquement en mode marketplace)
          ══════════════════════════════════════════════════════════════════════ */}
      {isMarketplace && (
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between h-14">
              <Link to="/marketplace" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
                <ArrowLeft size={18} />
                <span className="hidden sm:inline">Retour au marketplace</span>
              </Link>
              
              <Link to="/" className="flex items-center gap-2">
                <span className="font-bold text-lg">
                  <span className="text-blue-600">Abracada</span>
                  <span className="text-orange-500">World</span>
                </span>
              </Link>
              
              <div className="flex items-center gap-3">
                <Link to="/marketplace" className="p-2 text-gray-500 hover:text-gray-700">
                  <Search size={20} />
                </Link>
                <Link to="/panier" className="p-2 text-gray-500 hover:text-gray-700">
                  <ShoppingBag size={20} />
                </Link>
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          HEADER BOUTIQUE
          ══════════════════════════════════════════════════════════════════════ */}
      <header className="relative">
        {/* Cover Image */}
        <div 
          className="h-48 sm:h-64 md:h-80 bg-gradient-to-r from-blue-600 to-blue-800"
          style={{
            backgroundImage: store.cover_image_url ? `url(${store.cover_image_url})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor: !store.cover_image_url ? primaryColor : undefined
          }}
        />
        
        {/* Logo et infos */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="relative -mt-16 sm:-mt-20 pb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
              {/* Logo */}
              <div 
                className="w-28 h-28 sm:w-36 sm:h-36 rounded-xl bg-white shadow-lg border-4 border-white overflow-hidden flex items-center justify-center"
                style={{ borderColor: primaryColor }}
              >
                {store.logo_url ? (
                  <img 
                    src={store.logo_url} 
                    alt={store.name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-4xl sm:text-5xl font-bold" style={{ color: primaryColor }}>
                    {store.name?.charAt(0) || '?'}
                  </span>
                )}
              </div>
              
              {/* Infos */}
              <div className="flex-1 pt-2 sm:pt-0 sm:pb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    {store.name}
                  </h1>
                  {store.is_verified && (
                    <CheckCircle className="text-blue-500" size={24} />
                  )}
                </div>
                
                {store.tagline && (
                  <p className="text-gray-600 mt-1">{store.tagline}</p>
                )}
                
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 flex-wrap">
                  {store.category && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full">
                      {store.category}
                    </span>
                  )}
                  {store.city && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={14} />
                      {store.city}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <Star size={14} className="text-yellow-500 fill-yellow-500" />
                    {reviews?.length || 0} avis
                  </span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-2 mt-4 sm:mt-0">
                <button className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition">
                  <Heart size={20} className="text-gray-600" />
                </button>
                <button className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition">
                  <Share2 size={20} className="text-gray-600" />
                </button>
                <a
                  href={`#contact`}
                  className="px-6 py-2.5 rounded-lg font-medium text-white transition"
                  style={{ backgroundColor: primaryColor }}
                >
                  Contacter
                </a>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ══════════════════════════════════════════════════════════════════════
          ONGLETS
          ══════════════════════════════════════════════════════════════════════ */}
      <div className="border-b border-gray-200 bg-white sticky top-0 sm:top-14 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <nav className="flex gap-1 overflow-x-auto no-scrollbar">
            {[
              { id: 'about', label: 'À propos' },
              settings.show_services && { id: 'services', label: 'Services', count: services?.length },
              settings.show_products && { id: 'products', label: 'Produits', count: products?.length },
              { id: 'blog', label: 'Blog', count: storeData?.blog_posts?.length },
              { id: 'galerie', label: 'Galerie', count: storeData?.gallery?.length },
              settings.show_social_feed && { id: 'feed', label: 'Actualités', count: social_posts?.length },
              settings.show_reviews && { id: 'reviews', label: 'Avis', count: reviews?.length },
            ].filter(Boolean).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                  activeTab === tab.id
                    ? 'border-current text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
                style={activeTab === tab.id ? { color: primaryColor, borderColor: primaryColor } : {}}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-gray-100">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          CONTENU
          ══════════════════════════════════════════════════════════════════════ */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Section À propos */}
            {activeTab === 'about' && (
              <section className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 mb-4">À propos</h2>
                <p className="text-gray-600 whitespace-pre-line">
                  {store.description || "Pas de description disponible."}
                </p>
                
                {/* Liens sociaux */}
                {store.social_links && Object.values(store.social_links).some(v => v) && (
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Suivez-nous</h3>
                    <div className="flex gap-3">
                      {store.social_links.facebook && (
                        <a href={store.social_links.facebook} target="_blank" rel="noopener noreferrer"
                           className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition">
                          <Facebook size={20} />
                        </a>
                      )}
                      {store.social_links.instagram && (
                        <a href={store.social_links.instagram} target="_blank" rel="noopener noreferrer"
                           className="p-2 rounded-full bg-pink-100 text-pink-600 hover:bg-pink-200 transition">
                          <Instagram size={20} />
                        </a>
                      )}
                      {store.social_links.linkedin && (
                        <a href={store.social_links.linkedin} target="_blank" rel="noopener noreferrer"
                           className="p-2 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition">
                          <Linkedin size={20} />
                        </a>
                      )}
                      {store.social_links.youtube && (
                        <a href={store.social_links.youtube} target="_blank" rel="noopener noreferrer"
                           className="p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition">
                          <Youtube size={20} />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Section Services */}
            {activeTab === 'services' && (
              <section className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900">Nos services</h2>
                {services?.length > 0 ? (
                  <div className="grid gap-4">
                    {services.map(service => (
                      <div key={service.id} className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">{service.name}</h3>
                            {service.description && (
                              <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                            )}
                            {service.category && (
                              <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-gray-100 rounded-full text-gray-600">
                                {service.category}
                              </span>
                            )}
                          </div>
                          {service.price && (
                            <div className="text-right">
                              <span className="text-lg font-bold" style={{ color: primaryColor }}>
                                {service.price}€
                              </span>
                              {service.unit && (
                                <span className="text-sm text-gray-500">/{service.unit}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-8 text-center text-gray-500">
                    Pas de services disponibles pour le moment.
                  </div>
                )}
              </section>
            )}

            {/* Section Produits */}
            {activeTab === 'products' && (
              <section className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900">Nos produits</h2>
                {products?.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {products.map(product => (
                      <div key={product.id} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition group">
                        <div className="aspect-square bg-gray-100">
                          {product.image_url ? (
                            <img 
                              src={product.image_url} 
                              alt={product.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package size={32} className="text-gray-300" />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <h3 className="font-medium text-gray-900 text-sm truncate">{product.name}</h3>
                          <p className="text-lg font-bold mt-1" style={{ color: primaryColor }}>
                            {product.price}€
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-8 text-center text-gray-500">
                    Pas de produits disponibles pour le moment.
                  </div>
                )}
              </section>
            )}

            {/* Section Blog */}
            {activeTab === 'blog' && (
              <section className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900">Blog</h2>
                {storeData?.blog_posts?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {storeData.blog_posts.map((post, idx) => (
                      <article
                        key={post.id || idx}
                        className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                      >
                        {post.cover_image && (
                          <img
                            src={post.cover_image}
                            alt={post.title}
                            className="w-full h-48 object-cover"
                          />
                        )}
                        <div className="p-5">
                          {post.category && (
                            <span 
                              className="inline-block px-3 py-1 text-xs font-medium rounded-full mb-3"
                              style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                            >
                              {post.category}
                            </span>
                          )}
                          <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                            {post.title}
                          </h3>
                          <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                            {post.excerpt || post.content?.substring(0, 150)}...
                          </p>
                          <div className="flex items-center justify-between text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {new Date(post.created_at).toLocaleDateString('fr-FR', { 
                                day: 'numeric', 
                                month: 'long', 
                                year: 'numeric' 
                              })}
                            </span>
                            <span
                              className="font-medium hover:underline cursor-pointer"
                              style={{ color: primaryColor }}
                            >
                              Lire la suite →
                            </span>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-8 text-center">
                    <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Aucun article de blog pour le moment</p>
                  </div>
                )}
              </section>
            )}

            {/* Section Galerie */}
            {activeTab === 'galerie' && (
              <section className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900">Galerie photos</h2>
                {storeData?.gallery?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {storeData.gallery.map((photo, idx) => (
                      <div
                        key={photo.id || idx}
                        className="aspect-square rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer group relative"
                      >
                        <img
                          src={photo.image || photo.thumbnail}
                          alt={photo.title || 'Photo galerie'}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            {photo.title && (
                              <p className="text-white text-sm font-medium truncate">{photo.title}</p>
                            )}
                            <div className="flex items-center gap-3 mt-1 text-white/80 text-xs">
                              <span className="flex items-center gap-1">
                                <Heart size={12} />
                                {photo.likes || 0}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-8 text-center">
                    <Image size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Aucune photo dans la galerie</p>
                  </div>
                )}
              </section>
            )}

            {/* Section Feed Social */}
            {activeTab === 'feed' && (
              <section className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900">Actualités</h2>
                {social_posts?.length > 0 ? (
                  <div className="space-y-4">
                    {social_posts.map(post => (
                      <div key={post.id} className="bg-white rounded-xl p-5 shadow-sm">
                        <p className="text-gray-800 whitespace-pre-line">{post.content}</p>
                        {post.media_urls?.length > 0 && (
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            {post.media_urls.slice(0, 4).map((url, idx) => (
                              <img 
                                key={idx}
                                src={url} 
                                alt=""
                                className="rounded-lg w-full aspect-video object-cover"
                              />
                            ))}
                          </div>
                        )}
                        <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Heart size={14} />
                            {post.likes_count || 0}
                          </span>
                          <span>
                            {new Date(post.created_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-8 text-center text-gray-500">
                    Pas d'actualités pour le moment.
                  </div>
                )}
              </section>
            )}

            {/* Section Avis */}
            {activeTab === 'reviews' && (
              <section className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900">Avis clients</h2>
                {reviews?.length > 0 ? (
                  <div className="space-y-4">
                    {reviews.map(review => (
                      <div key={review.id} className="bg-white rounded-xl p-5 shadow-sm">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="font-medium text-gray-600">
                              {review.author_name?.charAt(0) || '?'}
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{review.author_name}</span>
                              <div className="flex">
                                {[...Array(5)].map((_, i) => (
                                  <Star 
                                    key={i}
                                    size={14} 
                                    className={i < (review.rating || 5) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                                  />
                                ))}
                              </div>
                            </div>
                            <p className="text-gray-600 mt-2">{review.content}</p>
                            <span className="text-xs text-gray-400 mt-2 block">
                              {new Date(review.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-8 text-center text-gray-500">
                    Pas d'avis pour le moment. Soyez le premier !
                  </div>
                )}
              </section>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            {/* Carte de contact */}
            <div id="contact" className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4">Contact</h3>
              
              <div className="space-y-3 text-sm">
                {store.address && (
                  <div className="flex items-start gap-3">
                    <MapPin size={18} className="text-gray-400 mt-0.5" />
                    <span className="text-gray-600">{store.address}, {store.postal_code} {store.city}</span>
                  </div>
                )}
                {store.phone && (
                  <a href={`tel:${store.phone}`} className="flex items-center gap-3 text-gray-600 hover:text-gray-900">
                    <Phone size={18} className="text-gray-400" />
                    {store.phone}
                  </a>
                )}
                {store.email && (
                  <a href={`mailto:${store.email}`} className="flex items-center gap-3 text-gray-600 hover:text-gray-900">
                    <Mail size={18} className="text-gray-400" />
                    {store.email}
                  </a>
                )}
              </div>
              
              {/* Formulaire de contact */}
              {settings.show_contact_form && (
                <div className="mt-6 pt-6 border-t border-gray-100">
                  {contactSent ? (
                    <div className="text-center py-4">
                      <CheckCircle size={40} className="mx-auto text-green-500 mb-2" />
                      <p className="font-medium text-green-700">Message envoyé !</p>
                      <p className="text-sm text-gray-500">Nous vous recontacterons bientôt.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleContactSubmit} className="space-y-3">
                      <input
                        type="text"
                        placeholder="Votre nom"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      <input
                        type="email"
                        placeholder="Votre email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      <input
                        type="tel"
                        placeholder="Téléphone (optionnel)"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      <textarea
                        placeholder="Votre message..."
                        required
                        rows={4}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                      />
                      <button
                        type="submit"
                        disabled={contactSending}
                        className="w-full py-3 rounded-lg font-medium text-white transition disabled:opacity-50"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {contactSending ? 'Envoi...' : 'Envoyer le message'}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>

            {/* Carte localisation (placeholder) */}
            {settings.show_location_map && store.address && (
              <div className="bg-white rounded-xl overflow-hidden shadow-sm">
                <div className="aspect-video bg-gray-200 flex items-center justify-center">
                  <MapPin size={32} className="text-gray-400" />
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-600">{store.address}</p>
                  <p className="text-sm text-gray-500">{store.postal_code} {store.city}</p>
                </div>
              </div>
            )}
          </aside>
        </div>
      </main>

      {/* ══════════════════════════════════════════════════════════════════════
          FOOTER
          ══════════════════════════════════════════════════════════════════════ */}
      <footer className="bg-white border-t border-gray-200 py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          {isMarketplace ? (
            <p className="text-sm text-gray-500">
              Propulsé par{' '}
              <Link to="/" className="font-medium text-blue-600 hover:underline">
                AbracadaWorld
              </Link>
              {' '}— Le marketplace des professionnels
            </p>
          ) : (
            <p className="text-xs text-gray-400">
              Propulsé par AbracadaWorld
            </p>
          )}
        </div>
      </footer>
    </div>
  );
}
